import awsconfig from 'stage.aws-exports';

// Get S3 bucket URL
const S3_BUCKET_URL = `https://${awsconfig.aws_user_files_s3_bucket}.s3.${
  awsconfig.aws_user_files_s3_bucket_region
}.amazonaws.com`;

/**
 * Demo style data with preset outfit descriptions
 * Each style includes:
 * - id: Unique identifier
 * - image: S3 URL for preview thumbnail (reference outfit photo)
 * - outfitJson: Outfit description for AI processing
 */
export const demoStyles = {
  men: [
    {
      id: 1,
      name: 'Smart Casual',
      image: `${S3_BUCKET_URL}/demo/try-on/man1.jpg`,
      outfitJson: {
        upper_garment: {
          type: 'sweater',
          color: 'charcoal grey',
          material: 'knit',
          fit: 'regular',
          length: 'hip',
          neckline: 'crew neck',
          sleeves: 'long, fitted',
          details: 'ribbed cuffs and hem',
        },
        lower_garment: {
          type: 'chinos',
          color: 'khaki brown',
          material: 'cotton blend',
          fit: 'regular',
          waist_height: 'mid-rise',
          details: 'front and back pockets, belt loops',
        },
        footwear: {
          type: 'sneakers',
          color: 'white',
          material: 'leather',
          details: 'white laces, flat sole',
        },
        accessories: [
          {
            type: 'belt',
            color: 'dark brown',
            material: 'leather',
            details: 'braided texture, silver buckle',
          },
          {
            type: 'watch',
            color: 'silver and brown',
            material: 'metal and leather',
            details: 'on left wrist',
          },
        ],
        overall_style:
          'The man is dressed in a smart casual outfit featuring a dark grey knit sweater and khaki chinos, complemented by white sneakers. The look is polished yet comfortable, suitable for various informal settings.',
      },
    },
    {
      id: 2,
      name: 'Business Elegant',
      image: `${S3_BUCKET_URL}/demo/try-on/man2.jpeg`,
      outfitJson: {
        upper_garment: {
          type: 'turtleneck and blazer',
          color: 'cream turtleneck, olive green blazer',
          material: 'knit (turtleneck), wool blend (blazer)',
          fit: 'fitted (turtleneck), tailored (blazer)',
          length: 'hip (turtleneck and blazer)',
          neckline: 'turtleneck',
          sleeves: 'long',
          details: 'single-breasted blazer with notched lapels',
        },
        lower_garment: {
          type: 'trousers',
          color: 'charcoal grey',
          material: 'wool blend',
          fit: 'slim',
          waist_height: 'mid-rise',
          details: 'flat front, no visible pleats',
        },
        footwear: {
          type: 'chelsea boots',
          color: 'dark brown',
          material: 'suede',
          details: 'ankle-height',
        },
        accessories: [
          {
            type: 'belt',
            color: 'dark brown',
            material: 'leather',
            details: 'silver buckle',
          },
          {
            type: 'watch',
            color: 'silver and blue',
            material: 'metal',
            details: 'on left wrist',
          },
        ],
        overall_style:
          'This is a sophisticated and smart-casual ensemble, featuring a cream turtleneck layered under an olive green blazer, paired with charcoal grey trousers and dark brown suede chelsea boots. The outfit is polished and modern, suitable for business casual or semi-formal occasions.',
      },
    },
    {
      id: 3,
      name: 'Casual Denim',
      image: `${S3_BUCKET_URL}/demo/try-on/man3.jpeg`,
      outfitJson: {
        upper_garment: {
          type: 't-shirt and denim shirt',
          color: 'light grey t-shirt, medium wash blue denim shirt',
          material: 'cotton (t-shirt), denim (shirt)',
          fit: 'loose (t-shirt), oversized (denim shirt)',
          length: 'long (t-shirt and denim shirt)',
          neckline: 'crew neck (t-shirt)',
          sleeves: 'short (t-shirt), long and rolled (denim shirt)',
          details: 'graphic print on t-shirt, button-up denim shirt worn open',
        },
        lower_garment: {
          type: 'jeans',
          color: 'medium wash blue',
          material: 'denim',
          fit: 'skinny',
          waist_height: 'mid-rise',
          details: 'distressed with rips on the knees and thighs',
        },
        footwear: {
          type: 'sneakers',
          color: 'black and white',
          material: 'canvas',
          details: 'white laces, white sole trim',
        },
        accessories: [
          {
            type: 'beanie',
            color: 'grey',
            material: 'knit',
            details: '',
          },
          {
            type: 'necklace',
            color: 'silver',
            material: 'metal',
            details: 'layered chains',
          },
          {
            type: 'watch',
            color: 'silver',
            material: 'metal',
            details: 'on left wrist',
          },
        ],
        overall_style:
          'This is a trendy and casual streetwear outfit, featuring a graphic t-shirt layered under an oversized denim shirt, paired with distressed skinny jeans and black and white sneakers. The look is completed with a grey beanie and silver chain necklaces, giving off a youthful and urban vibe.',
      },
    },
    {
      id: 4,
      name: 'Preppy Classic',
      image: `${S3_BUCKET_URL}/demo/try-on/man4.jpg`,
      outfitJson: {
        upper_garment: {
          type: 'button-up shirt and sweater',
          color: 'white (shirt), cream (sweater)',
          material: 'cotton (shirt), knit (sweater)',
          fit: 'regular (shirt), loose/oversized (sweater)',
          length: 'hip (both)',
          neckline: 'collared (shirt), crew neck (sweater)',
          sleeves: 'long (both), sweater sleeves pushed up',
          details: 'sweater layered over shirt, shirt collar visible',
        },
        lower_garment: {
          type: 'cargo pants',
          color: 'dark olive green',
          material: 'corduroy',
          fit: 'slim',
          waist_height: 'mid-rise',
          details: 'side cargo pockets, ribbed texture',
        },
        footwear: {
          type: 'chunky sneakers',
          color: 'off-white/cream',
          material: 'leather and synthetic',
          details: 'thick soles, white laces',
        },
        accessories: [
          {
            type: 'necklace',
            color: 'silver',
            material: 'metal',
            details: 'thick chain',
          },
          {
            type: 'bracelets',
            color: 'dark brown and black',
            material: 'leather',
            details: 'layered on left wrist',
          },
        ],
        overall_style:
          'This outfit combines casual comfort with a touch of modern street style, featuring a white button-up shirt layered under an oversized cream knit sweater, paired with dark olive green corduroy cargo pants and chunky off-white sneakers. The accessories, including a silver chain necklace and layered bracelets, add to the contemporary aesthetic.',
      },
    },
  ],
  women: [
    {
      id: 5,
      name: 'Elegant Chic',
      image: `${S3_BUCKET_URL}/demo/try-on/women1.jpeg`,
      outfitJson: {
        upper_garment: {
          type: 'knit top and blazer',
          color: 'cream (top), light lavender (blazer)',
          material: 'ribbed knit (top), unknown blend (blazer)',
          fit: 'fitted (top), oversized (blazer)',
          length: 'hip (both)',
          neckline: 'round neck (top), notched lapel (blazer)',
          sleeves: 'long (both)',
          details: 'blazer worn open',
        },
        lower_garment: {
          type: 'wide-leg trousers',
          color: 'olive green',
          material: 'unknown fabric, possibly a blend',
          fit: 'loose, wide-leg',
          waist_height: 'high-waisted',
          details: 'crease down the front of the leg',
        },
        footwear: {
          type: 'platform sneakers',
          color: 'white',
          material: 'leather and synthetic',
          details: 'chunky sole, white laces',
        },
        accessories: [
          {
            type: 'necklaces',
            color: 'gold',
            material: 'metal',
            details: 'layered, varying lengths',
          },
          {
            type: 'earrings',
            color: 'gold and green',
            material: 'metal and stone/bead',
            details: 'dangly',
          },
          {
            type: 'rings',
            color: 'gold',
            material: 'metal',
            details: 'on right hand',
          },
        ],
        overall_style:
          'This outfit presents a chic and contemporary look, blending formal and casual elements with an oversized lavender blazer over a cream ribbed knit top, paired with high-waisted olive green wide-leg trousers and white platform sneakers. The layered gold necklaces and green earrings add a stylish touch.',
      },
    },
    {
      id: 6,
      name: 'Casual Boho',
      image: `${S3_BUCKET_URL}/demo/try-on/women2.jpeg`,
      outfitJson: {
        upper_garment: {
          type: 'sweater',
          color: 'off-white/cream',
          material: 'knit',
          fit: 'relaxed',
          length: 'hip',
          neckline: 'round neck',
          sleeves: 'long, slightly dropped shoulder',
          details: 'ribbed cuffs and hem',
        },
        lower_garment: {
          type: 'trousers',
          color: 'camel brown',
          material: 'wool blend',
          fit: 'straight-leg, relaxed',
          waist_height: 'mid-rise',
          details: 'crease down the front of the leg, side pockets',
        },
        footwear: {
          type: 'ankle boots',
          color: 'camel brown',
          material: 'suede or leather',
          details: 'block heel, pointed toe',
        },
        accessories: [
          {
            type: 'necklace',
            color: 'gold',
            material: 'metal',
            details: 'chunky chain',
          },
          {
            type: 'bracelet',
            color: 'gold',
            material: 'metal',
            details: 'chunky chain on right wrist',
          },
        ],
        overall_style:
          'This outfit showcases a sophisticated and effortlessly chic aesthetic, combining a relaxed off-white knit sweater with tailored camel brown straight-leg trousers and matching ankle boots. The chunky gold chain necklace and bracelet add a touch of modern elegance to the neutral palette.',
      },
    },
    {
      id: 7,
      name: 'Office Professional',
      image: `${S3_BUCKET_URL}/demo/try-on/women3.jpeg`,
      outfitJson: {
        upper_garment: {
          type: 'sweater',
          color: 'olive green',
          material: 'knit',
          fit: 'relaxed',
          length: 'hip',
          neckline: 'round neck',
          sleeves: 'long',
          details: 'ribbed cuffs and hem, slightly textured knit',
        },
        lower_garment: {
          type: 'trousers',
          color: 'cream/off-white',
          material: 'linen blend',
          fit: 'relaxed, tapered',
          waist_height: 'high-waisted',
          details: 'front pleats, side pockets, cuffed hem',
        },
        footwear: {
          type: 'sneakers',
          color: 'white',
          material: 'leather',
          details: 'white laces, flat sole',
        },
        accessories: [
          {
            type: 'necklaces',
            color: 'gold',
            material: 'metal',
            details: 'layered with small pendants',
          },
          {
            type: 'ring',
            color: 'gold',
            material: 'metal',
            details: 'on right hand',
          },
          {
            type: 'watch',
            color: 'gold',
            material: 'metal',
            details: 'on left wrist',
          },
        ],
        overall_style:
          'This outfit blends casual elegance with comfort, featuring a relaxed olive green knit sweater tucked into high-waisted cream linen trousers, paired with white sneakers. The layered gold necklaces and gold watch add a touch of sophistication to this stylish, everyday look.',
      },
    },
    {
      id: 8,
      name: 'Summer Casual',
      image: `${S3_BUCKET_URL}/demo/try-on/women4.jpeg`,
      outfitJson: {
        upper_garment: {
          type: 'sweater',
          color: 'cream/off-white',
          material: 'knit',
          fit: 'relaxed',
          length: 'hip',
          neckline: 'round neck',
          sleeves: 'long',
          details: 'ribbed cuffs and hem',
        },
        lower_garment: {
          type: 'wide-leg trousers',
          color: 'olive green',
          material: 'linen blend',
          fit: 'loose, wide-leg',
          waist_height: 'high-waisted',
          details: 'front pleats',
        },
        footwear: {
          type: 'ankle boots',
          color: 'cognac brown',
          material: 'leather',
          details: 'block heel',
        },
        accessories: [
          {
            type: 'scarf',
            color: 'multi-colored (rust, blue, orange, brown)',
            material: 'wool blend',
            details: 'patterned, fringed edges',
          },
          {
            type: 'necklace',
            color: 'gold',
            material: 'metal',
            details: 'layered with small pendant',
          },
          {
            type: 'watch',
            color: 'rose gold with brown strap',
            material: 'metal and leather',
            details: 'on left wrist',
          },
          {
            type: 'rings',
            color: 'gold',
            material: 'metal',
            details: 'on fingers of both hands',
          },
          {
            type: 'earrings',
            color: 'gold',
            material: 'metal',
            details: 'small hoops',
          },
        ],
        overall_style:
          'This is a cozy and stylish autumn-inspired outfit, featuring a cream knit sweater and olive green wide-leg trousers, elevated by a vibrant patterned scarf. Cognac brown ankle boots and delicate gold jewelry complete the comfortable yet chic ensemble.',
      },
    },
  ],
};
